import { useCallback, useEffect, useMemo, useState } from 'react';

import { Header } from '~/client/components/Header';
import { HostView } from '~/client/components/HostView';
import { RespondView } from '~/client/components/RespondView';
import { ResultView } from '~/client/components/ResultView';
import { ShareSheet } from '~/client/components/ShareSheet';
import { ApiError, createRespondent, getPoll, updateRespondent } from '~/client/lib/api';
import type { ImageGenParams } from '~/client/lib/imageGen';
import { rememberPoll } from '~/client/lib/myPolls';
import {
  clearDraft,
  getDraft,
  getOwnerToken,
  getRespondent,
  saveRespondent,
  type RespondentRecord,
} from '~/client/lib/tokens';
import { formatDayLabel, formatRangeLabel } from '~/shared/dates';
import { buildDensity, everyoneFreeRuns, topOverlap } from '~/shared/density';
import type { PollWithRespondents } from '~/shared/types';

import { Expired } from './Expired';
import { NotFound } from './NotFound';

interface PollPageProps {
  pollId: string;
}

type PollState =
  | { status: 'loading' }
  | { status: 'not_found' }
  | { status: 'expired' }
  | { status: 'error'; message: string }
  | { status: 'ok'; data: PollWithRespondents };

type Mode = 'host' | 'respond' | 'result';

function consumeEditQueryParam(): RespondentRecord | null {
  if (typeof window === 'undefined') return null;
  const url = new URL(window.location.href);
  const r = url.searchParams.get('r');
  if (!r) return null;
  const [id, token] = r.split('.');
  if (!id || !token) return null;
  url.searchParams.delete('r');
  window.history.replaceState({}, '', url.pathname + (url.search ? `?${url.searchParams}` : ''));
  return { id, token };
}

function buildImageParams(data: PollWithRespondents, url: string): ImageGenParams | null {
  const respondents = data.respondents;
  if (respondents.length === 0) return null;
  const density = buildDensity(respondents);
  const total = respondents.length;
  const runs = everyoneFreeRuns(density, total);
  const sortedRuns = [...runs].sort((a, b) => {
    if (b.days.length !== a.days.length) return b.days.length - a.days.length;
    return a.start < b.start ? -1 : 1;
  });
  const longest = sortedRuns[0];

  if (longest) {
    return {
      pollTitle: data.poll.title,
      prefix: "you're all free",
      highlight: formatRangeLabel(longest.start, longest.end),
      monthYear: {
        year: Number(longest.start.slice(0, 4)),
        month: Number(longest.start.slice(5, 7)) - 1,
      },
      highlightDays: new Set(longest.days),
      url,
    };
  }

  const top = topOverlap(density, 1);
  const best = top[0];
  if (best) {
    return {
      pollTitle: data.poll.title,
      prefix: 'best so far',
      highlight: formatDayLabel(best.day),
      monthYear: {
        year: Number(best.day.slice(0, 4)),
        month: Number(best.day.slice(5, 7)) - 1,
      },
      highlightDays: new Set([best.day]),
      url,
    };
  }
  return null;
}

export function Poll({ pollId }: PollPageProps) {
  const [state, setState] = useState<PollState>({ status: 'loading' });
  const [respondent, setRespondent] = useState<RespondentRecord | null>(() => {
    const fromUrl = consumeEditQueryParam();
    if (fromUrl) {
      saveRespondent(pollId, fromUrl);
      return fromUrl;
    }
    return getRespondent(pollId);
  });
  const isOwner = useMemo(() => Boolean(getOwnerToken(pollId)), [pollId]);
  const [forceRespondMode, setForceRespondMode] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [excludedIds, setExcludedIds] = useState<Set<string>>(new Set());

  const refresh = useCallback(async () => {
    try {
      const data = await getPoll(pollId);
      setState({ status: 'ok', data });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 404) setState({ status: 'not_found' });
        else if (err.status === 410) setState({ status: 'expired' });
        else setState({ status: 'error', message: err.message });
      } else {
        setState({ status: 'error', message: 'failed to load poll' });
      }
    }
  }, [pollId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (state.status !== 'ok') return;
    const role = isOwner ? 'owner' : respondent ? 'respondent' : 'visitor';
    rememberPoll({ id: state.data.poll.id, title: state.data.poll.title, role });
  }, [state, isOwner, respondent]);

  const mode: Mode = useMemo(() => {
    if (forceRespondMode) return 'respond';
    if (respondent) return 'result';
    if (isOwner) return 'host';
    return 'respond';
  }, [forceRespondMode, respondent, isOwner]);

  const toggleExcluded = useCallback((id: string) => {
    setExcludedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const resetExcluded = useCallback(() => {
    setExcludedIds(new Set());
  }, []);

  if (state.status === 'loading') {
    return (
      <>
        <Header />
        <main className="app-main center">
          <div className="empty-state">
            <p>loading…</p>
          </div>
        </main>
      </>
    );
  }
  if (state.status === 'not_found') return <NotFound />;
  if (state.status === 'expired') return <Expired />;
  if (state.status === 'error') {
    return (
      <>
        <Header />
        <main className="app-main center">
          <div className="empty-state">
            <h2>couldn't load this</h2>
            <p>{state.message}</p>
          </div>
        </main>
      </>
    );
  }

  const { data } = state;
  const { poll } = data;
  const allRespondents = data.respondents;
  const visibleRespondents = allRespondents.filter((r) => !excludedIds.has(r.id));
  const effectiveData: PollWithRespondents = {
    poll,
    respondents: visibleRespondents,
  };
  const me = respondent ? allRespondents.find((r) => r.id === respondent.id) : null;
  const shareUrl = `${window.location.origin}/p/${poll.id}`;
  const editLink = respondent
    ? `${window.location.origin}/p/${poll.id}?r=${respondent.id}.${respondent.token}`
    : undefined;
  const imageParams = buildImageParams(effectiveData, shareUrl);

  const handleSubmitNew = async (name: string, yes: string[], maybe: string[]) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await createRespondent(poll.id, { name, yes, maybe });
      const record: RespondentRecord = { id: result.id, token: result.editToken };
      saveRespondent(poll.id, record);
      clearDraft(poll.id);
      setRespondent(record);
      setForceRespondMode(false);
      await refresh();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'failed to save';
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitEdit = async (name: string, yes: string[], maybe: string[]) => {
    if (!respondent) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await updateRespondent(poll.id, respondent.id, respondent.token, {
        name,
        yes,
        maybe,
      });
      setForceRespondMode(false);
      await refresh();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'failed to save';
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      {mode === 'host' && (
        <HostView
          data={effectiveData}
          allRespondents={allRespondents}
          excludedIds={excludedIds}
          onToggleExcluded={toggleExcluded}
          onResetExcluded={resetExcluded}
          onShare={() => setShowShare(true)}
          onRespondAs={() => setForceRespondMode(true)}
        />
      )}
      {mode === 'result' && me && (
        <ResultView
          data={effectiveData}
          allRespondents={allRespondents}
          excludedIds={excludedIds}
          onToggleExcluded={toggleExcluded}
          onResetExcluded={resetExcluded}
          selfRespondentId={me.id}
          onEdit={() => setForceRespondMode(true)}
          onShare={() => setShowShare(true)}
        />
      )}
      {mode === 'respond' &&
        (() => {
          const draft = !me ? getDraft(poll.id) : null;
          const initialName = me?.name ?? draft?.name ?? '';
          const initialYes = me ? new Set(me.yes) : draft ? new Set(draft.yes) : new Set<string>();
          const initialMaybe = me
            ? new Set(me.maybe)
            : draft
              ? new Set(draft.maybe)
              : new Set<string>();
          return (
            <RespondView
              poll={poll}
              initialName={initialName}
              initialYes={initialYes}
              initialMaybe={initialMaybe}
              isEditing={Boolean(me)}
              submitting={submitting}
              error={submitError ?? undefined}
              onSubmit={async (name, yes, maybe) => {
                if (respondent && me) {
                  await handleSubmitEdit(name, yes, maybe);
                } else {
                  await handleSubmitNew(name, yes, maybe);
                }
              }}
              onCancel={
                isOwner || me
                  ? () => {
                      setForceRespondMode(false);
                      setSubmitError(null);
                    }
                  : undefined
              }
            />
          );
        })()}
      {showShare && (
        <ShareSheet
          url={shareUrl}
          title={poll.title}
          editLink={editLink}
          imageParams={imageParams}
          onClose={() => setShowShare(false)}
        />
      )}
    </>
  );
}
